/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderNavigationService, BuilderUserContext } from '@gh/builder-shared';
import { BuilderPermission, LetterTemplateLevel, LetterTemplateType } from '@gh/builder-shared-data';
import { DataSet, getRouteInitData, getRouteStaticData, HelpID } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, NotificationService, WaitingService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, switchMap$ } from '@gh/rx/operators';
import { assign } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { LetterTemplateBusinessService } from '../../resources/letter-template-business.service';
import { getLetterTemplateId, LetterTemplate, letterTemplateComparator } from '../../resources/letter-template.model';
import { LetterTemplateGridDefinition } from './letter-template-grid.service';
import { ltValidationResultDialogRoute } from './lt-validation-result-dialog/lt-validation-result-dialog.component';

@Component({
  selector: 'gh-letter-template-list-tab',
  template: require('./letter-template-list-tab.component.html'),
  providers: [LetterTemplateGridDefinition],
})
export class LetterTemplateListTabComponent implements OnInit {
  type: LetterTemplateType;
  dataSet: DataSet<LetterTemplate>;

  selected$: Observable<Maybe<LetterTemplate>>;
  selectedId$$ = new Subject<ID>();
  editable: boolean;

  constructor(private route: ActivatedRoute,
              private dialogService: DialogService,
              private userContext: BuilderUserContext,
              private navigationService: BuilderNavigationService,
              public gridDefinition: LetterTemplateGridDefinition,
              private business: LetterTemplateBusinessService,
              private waitingService: WaitingService,
              private notificationService: NotificationService) {
  }

  get titleLabel(): string {
    return this.isJob ? 'lbl_job_letters_templates' : 'lbl_system_letter_templates';
  }

  get isJob(): boolean {
    return this.type === LetterTemplateType.Job;
  }

  get helpId(): HelpID {
    return this.type === LetterTemplateType.Job
      ? 'setup/manager/letter-templates/job'
      : 'setup/manager/letter-templates/system';
  }

  ngOnInit(): void {
    const { templates, ...init } = getRouteInitData(this.route);
    assign(this, init);

    this.type = getRouteStaticData(this.route)['type'];
    this.editable = this.userContext.hasPermission(this.isJob
      ? BuilderPermission.Setup.ManageTemplates.JobLetters.MANAGE
      : BuilderPermission.Setup.ManageTemplates.SystemLetters.MANAGE);
    this.dataSet = new DataSet<LetterTemplate>(getLetterTemplateId, letterTemplateComparator, templates);

    this.selected$ = this.selectedId$$.pipe(switchMap$((id) => this.dataSet.byIdAsObservable(id)));
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityType: this.isJob ? 'JOB_LETTER' : 'SYSTEM_LETTER',
      label: 'lbl_manage_letter_templates',
    });
  }

  onTemplateSelected(template: LetterTemplate): void {
    this.selectedId$$.next(template.uniqueId);
  }

  onCopyToDraft(template: LetterTemplate, level: LetterTemplateLevel): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_letter_template_copy', { fileName: template.fileName })).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.makeDraft(template,
                                               level).pipe((this.waitingService.operator<LetterTemplate>()))))
      .subscribe((updatedTemplate) => this.dataSet.update(updatedTemplate), this.notificationService.processAll);
  }

  onCopyToOwn(template: LetterTemplate): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_letter_template_copy', { fileName: template.fileName })).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.completeDraft(template).pipe((this.waitingService.operator<LetterTemplate>()))))
      .subscribe((updatedTemplate) => this.dataSet.update(updatedTemplate), this.notificationService.processAll);
  }

  onPushDown(template: LetterTemplate): void {
    this.dialogService
      .confirmation(yesNoConfirmation(
        this.userContext.masterView ? 'msg_letter_template_push_down_office' : 'msg_letter_template_push_down_master',
        { fileName: template.fileName })).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.pushDown(template).pipe((this.waitingService.operator<LetterTemplate>()))))
      .subscribe((updatedTemplate) => this.dataSet.update(updatedTemplate), this.notificationService.processAll);
  }

  onValidate(template: LetterTemplate, level: LetterTemplateLevel, isDraft: boolean): void {
    this.business.validate(template, level, isDraft).pipe(
      (this.waitingService.operator()))
      .subscribe(
        (result) => {
          this.dialogService.openWithInput(ltValidationResultDialogRoute, result);
        },
        this.notificationService.processAll);
  }
}
